import { HttpClient } from '@angular/common/http';
import { IBaseEntityService } from '../../interfaces/entities/base-entity-service.interface';
import { EntityBase } from '../../models/entities/entity-base';
import { environment } from '../../../environments/environment';

/**
 * Abstract base service for entity CRUD operations
 * @template TEntity - The entity type that extends EntityBase
 */
export abstract class BaseEntityService<TEntity extends EntityBase> implements IBaseEntityService<TEntity> {
  /**
   * Array to store entities in memory
   * @protected
   */
  protected entities: TEntity[] = [];
  protected serviceUrl = environment.apiUrl;

  constructor(protected http: HttpClient | undefined | null = null) {
    this.initializeData();
    this.serviceUrl;
  }

  /**
   * Generate a GUID (Globally Unique Identifier)
   * @returns {string} A new GUID
   * @protected
   */
  protected generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Set the creation audit properties for a new entity
   * @protected
   * @param {TEntity} entity - The entity to set properties on
   * @param {string} createdBy - The user identifier who is creating the entity
   */
  protected setCreateAuditProperties(entity: TEntity, createdBy: string): void {
    const now = new Date();
    entity.id = entity.id || this.generateGuid();
    entity.createdBy = createdBy;
    entity.createdOn = now;
    entity.updatedBy = createdBy;
    entity.updatedOn = now;
    entity.isActive = true;
  }

  /**
   * Set the update audit properties for an existing entity
   * @protected
   * @param {TEntity} entity - The entity to set properties on
   * @param {string} updatedBy - The user identifier who is updating the entity
   */
  protected setUpdateAuditProperties(entity: TEntity, updatedBy: string): void {
    entity.updatedBy = updatedBy;
    entity.updatedOn = new Date();
  }

  /**
   * Initialize data for the service
   * Must be implemented by derived classes
   * @abstract
   * @protected
   */
  protected abstract initializeData(): void;

  /**
   * Create a new entity
   * Must be implemented by derived classes
   * @abstract
   * @param {TEntity} entity - The entity to create
   * @param {string} createdBy - The user identifier who is creating the entity
   * @returns {TEntity} The created entity with assigned ID
   */
  abstract createEntity(entity: TEntity, createdBy: string): TEntity;

  /**
   * Update an existing entity
   * Must be implemented by derived classes
   * @abstract
   * @param {string} id - The ID of the entity to update
   * @param {Partial<TEntity>} updates - The properties to update
   * @param {string} updatedBy - The user identifier who is updating the entity
   * @returns {TEntity | null} The updated entity if found, null otherwise
   */
  abstract updateEntity(id: string, updates: Partial<TEntity>, updatedBy: string): TEntity | null;

  /**
   * Get all entities
   * @returns {TEntity[]} Array of all entities
   */
  getAllEntities(): TEntity[] {
    return [...this.entities];
  }

  /**
   * Get an entity by ID
   * @param {string} id - The entity ID
   * @returns {TEntity | undefined} The entity if found, undefined otherwise
   */
  getEntityById(id: string): TEntity | undefined {
    return this.entities.find(entity => entity.id === id);
  }

  /**
   * Delete an entity by ID (soft delete - sets isActive to false)
   * @param {string} id - The ID of the entity to delete
   * @param {string} deletedBy - The user identifier who is deleting the entity
   * @returns {boolean} True if deleted successfully, false if not found
   */
  deleteEntity(id: string, deletedBy: string): boolean {
    const entity = this.getEntityById(id);

    if (!entity) {
      return false;
    }

    entity.isActive = false;
    this.setUpdateAuditProperties(entity, deletedBy);
    return true;
  }

  /**
   * Permanently delete an entity by ID (hard delete - removes from array)
   * @param {string} id - The ID of the entity to permanently delete
   * @returns {boolean} True if deleted successfully, false if not found
   */
  permanentlyDeleteEntity(id: string): boolean {
    const index = this.entities.findIndex(entity => entity.id === id);

    if (index === -1) {
      return false;
    }

    this.entities.splice(index, 1);
    return true;
  }

  /**
   * Get all active entities (isActive = true)
   * @returns {TEntity[]} Array of active entities
   */
  getActiveEntities(): TEntity[] {
    return this.entities.filter(entity => entity.isActive);
  }

  /**
   * Get all inactive entities (isActive = false)
   * @returns {TEntity[]} Array of inactive entities
   */
  getInactiveEntities(): TEntity[] {
    return this.entities.filter(entity => !entity.isActive);
  }
}

